<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { authService } from '@exchange/libs/auth/service/src';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { marketService, MarketState } from '@exchange/libs/market/service/src';
import { useOrderbook } from '@exchange/libs/orderbook/service/src';
import MarketOverlay from '@exchange/libs/overlays/MarketOverlay.vue';

import DepthChart from './DepthChart.vue';
import useDepthChart from './useDepthChart';

const props = defineProps({
  marketId: { type: String, required: true },
  showTitle: { type: Boolean, default: true },
});

const { marketId } = toRefs(props);

const { t } = useI18n({ useScope: 'global' });
const { isXS } = useMQBreakpoints();

const { getMarket, getMarketPrecision, getMarketName, getMarketState } = marketService;

const market = computed(() => getMarket(marketId.value));
const marketState = computed(() => getMarketState(market.value));
const marketName = computed(() => getMarketName(market.value));
const marketPrecisions = computed(() => getMarketPrecision(market.value));

const depthChartOverlayFor = computed(() => (isXS.value ? [MarketState.NEW] : []));
const showDepthChartOverlay = computed(() => marketState.value && depthChartOverlayFor.value.includes(marketState.value));
const { orderbook, orderbookIsLoading } = useOrderbook(marketId);
const hasBeenAuthenticated = computed(() => authService.hasBeenAuthenticated);

const { showLoading, onRendererReady } = useDepthChart(orderbookIsLoading);

const title = computed(() => {
  if (!isXS.value) {
    return t('modules.tradingChart.tabs.depthChart');
  }

  return undefined;
});
</script>

<template>
  <ot-module-container
    class="depth-chart-container"
    :loading="showLoading"
    :title="showTitle ? title : ''"
  >
    <template #main-content>
      <div class="depth-chart-container__content">
        <market-overlay
          v-if="showDepthChartOverlay"
          :market-name="marketName"
          :market-state="marketState"
          :has-been-authenticated="hasBeenAuthenticated"
        />
        <depth-chart
          v-if="market && orderbook"
          :market-id="marketId"
          :market-base-code="market && market.base.code"
          :amount-precision="marketPrecisions.amount"
          :market-precision="marketPrecisions.market"
          :orderbook="orderbook"
          @renderer-ready="onRendererReady"
        />
      </div>
    </template>
  </ot-module-container>
</template>

<style lang="scss">
.depth-chart-container {
  &__content {
    display: contents;
  }

  border: 0;
}
</style>
